import React, { useState, useMemo, useEffect } from 'react';
import { User, signOut } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { useBudgetData } from '../hooks/useBudgetData';
import { LogOut, Plus, Wallet, TrendingUp, TrendingDown, Target, Clock, CheckCircle2, Bot, CreditCard, Banknote, Heart, Trash2, Edit2, ChevronDown, Settings as SettingsIcon, Flag, Calendar, Scale, Users, AlertTriangle, PiggyBank, LineChart, Home, UploadCloud, Download, RefreshCw, Sparkles, Check } from 'lucide-react';
import { format, isSameMonth, formatDistanceToNow } from 'date-fns';
import TransactionModal from './TransactionModal';
import GoalModal from './GoalModal';
import DebtModal from './DebtModal';
import BankAccountModal from './BankAccountModal';
import AccountFeeModal from './AccountFeeModal';
import CreditCardModal from './CreditCardModal';
import BudgetModal from './BudgetModal';
import WishlistModal from './WishlistModal';
import Charts from './Charts';
import ProjectionChart from './ProjectionChart';
import AIDashboard from './AIDashboard';
import { doc, updateDoc, deleteDoc, increment, collection, query, where, onSnapshot, writeBatch } from 'firebase/firestore';
import { suggestCategories } from '../lib/suggestCategories';
import { GENERIC_IMPORT_CATEGORIES } from '../lib/csvStatementParser';
import { Goal, Schedule, Transaction, Debt, IncomeStream, RecurringExpense } from '../types';
import ExpectedIncome from './ExpectedIncome';
import Settings from './Settings';
import Vault from './Vault';
import InfoTooltip from './InfoTooltip';
import AIChatModal from './AIChatModal';
import { MessageSquare } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import GoalsPlanner from './GoalsPlanner';
import PlannedPaymentModal from './PlannedPaymentModal';
import PlanningScheduleDashboard from './PlanningScheduleDashboard';
import NetWorth from './NetWorth';
import StressTester from './StressTester';

import DebtCrush from './DebtCrush';
import ReconcileDashboard from './ReconcileDashboard';
import PlaidConnectButton from './PlaidConnectButton';
import PlaidAccountMapper from './PlaidAccountMapper';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

type Tab = 'overview' | 'bank_financials' | 'debts_budget' | 'ai_strategy' | 'settings' | 'vault' | 'goals_planner' | 'planning_schedule' | 'net_worth' | 'debt_crush' | 'reconcile';

function csvEscape(value: string | number | undefined | null): string {
  const str = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
}

function downloadCSV(filename: string, rows: (string | number)[][]) {
  // Leading BOM so Excel (Windows in particular) reads the file as UTF-8
  // instead of guessing a legacy codepage and mangling any non-ASCII text.
  const csvContent = '﻿' + rows.map(row => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const formatSchedule = (schedule?: Schedule) => {
  if (!schedule) return 'No schedule';
  if (schedule.frequency === 'monthly') return `Monthly (Day ${schedule.date1})`;
  if (schedule.frequency === 'semi-monthly') return `Semi-Monthly (Days ${schedule.date1} & ${schedule.date2})`;
  if (schedule.frequency === 'bi-weekly') {
    return schedule.nextDate ? `Bi-weekly (Next: ${format(schedule.nextDate, 'MMM dd')})` : 'Bi-weekly';
  }
  if (schedule.frequency === 'weekly') {
    return schedule.nextDate ? `Weekly (Next: ${format(schedule.nextDate, 'MMM dd')})` : 'Weekly';
  }
  return 'Unknown';
};

interface PlaidItemDoc {
  id: string;
  institutionName: string;
  needsReconnect: boolean;
  lastSyncAt: Date | null;
  lastSyncOk: boolean | null;
  lastSyncMessage: string | null;
}

export default function Dashboard({ user, householdId }: { user: User, householdId: string }) {
  const { bankAccounts, transactions, goals, debts, incomes, recurringExpenses, wishlist, reports, plannedPayments, paymentFiles, dismissedOccurrences, bankStatements, loading } = useBudgetData(householdId);
  
  const [currentTab, setCurrentTab] = useState<Tab>('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [transactionFilter, setTransactionFilter] = useState<'all' | 'income' | 'expense' | 'spend' | 'debt_payment' | 'savings_contribution' | 'transfer'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | '30d' | '90d' | '6m' | '1y'>('all');

  const filteredTransactions = useMemo(() => {
    let result = transactions;
    if (transactionFilter !== 'all') {
      result = result.filter(tx => tx.type === transactionFilter);
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      if (dateFilter === '30d') cutoffDate.setDate(now.getDate() - 30);
      else if (dateFilter === '90d') cutoffDate.setDate(now.getDate() - 90);
      else if (dateFilter === '6m') cutoffDate.setMonth(now.getMonth() - 6);
      else if (dateFilter === '1y') cutoffDate.setFullYear(now.getFullYear() - 1);
      result = result.filter(tx => tx.date && tx.date >= cutoffDate);
    }
    return result;
  }, [transactions, transactionFilter, dateFilter]);

  // Transactions still sitting on one of the generic, non-descriptive
  // import-default categories (see GENERIC_IMPORT_CATEGORIES) -- these are
  // the ones a "Suggest categories" pass can actually improve. Scoped to
  // whatever's currently filtered/visible, same as the section it lives in.
  const genericCategoryTransactions = useMemo(
    () => filteredTransactions.filter(tx => GENERIC_IMPORT_CATEGORIES.has(tx.category)),
    [filteredTransactions]
  );
  const [suggestingCategories, setSuggestingCategories] = useState(false);
  const [categorySuggestError, setCategorySuggestError] = useState<string | null>(null);
  // Candidate suggestions awaiting review -- never applied until the user
  // explicitly hits Apply below, same human-in-the-loop posture as the
  // statement review table's own "Suggest categories".
  const [pendingCategorySuggestions, setPendingCategorySuggestions] = useState<{ txId: string; description: string; oldCategory: string; newCategory: string }[] | null>(null);
  const [acceptedSuggestionIds, setAcceptedSuggestionIds] = useState<Record<string, boolean>>({});
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);

  const handleSuggestTransactionCategories = async () => {
    setSuggestingCategories(true);
    setCategorySuggestError(null);
    try {
      // useBudgetData's return type doesn't narrow past `any` here (a
      // pre-existing gap, not new) -- cast restores real typing for this
      // expression so .filter's type predicate narrows correctly instead
      // of collapsing to unknown[].
      const existingCategories: string[] = [...new Set(
        (transactions as Transaction[]).map(t => t.category).filter((c): c is string => !!c && !GENERIC_IMPORT_CATEGORIES.has(c))
      )];
      const suggestions = await suggestCategories(
        genericCategoryTransactions.map(tx => ({
          rowId: tx.id!,
          description: tx.description || tx.category,
          amount: tx.amount,
          direction: tx.type === 'income' ? 'credit' as const : 'debit' as const,
        })),
        existingCategories
      );
      const candidates = genericCategoryTransactions
        .filter(tx => suggestions[tx.id!] && suggestions[tx.id!] !== tx.category)
        .map(tx => ({ txId: tx.id!, description: tx.description || tx.category, oldCategory: tx.category, newCategory: suggestions[tx.id!] }));
      setPendingCategorySuggestions(candidates);
      setAcceptedSuggestionIds(Object.fromEntries(candidates.map(c => [c.txId, true])));
    } catch (err: any) {
      setCategorySuggestError(err.message || 'Could not get category suggestions.');
    } finally {
      setSuggestingCategories(false);
    }
  };

  const handleApplyCategorySuggestions = async () => {
    if (!pendingCategorySuggestions) return;
    const accepted = pendingCategorySuggestions.filter(c => acceptedSuggestionIds[c.txId]);
    if (accepted.length === 0) { setPendingCategorySuggestions(null); return; }
    setApplyingSuggestions(true);
    try {
      const batch = writeBatch(db);
      for (const c of accepted) {
        batch.update(doc(db, 'transactions', c.txId), { category: c.newCategory });
      }
      await batch.commit();
      setPendingCategorySuggestions(null);
    } catch (err: any) {
      setCategorySuggestError(err.message || 'Could not apply category suggestions.');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  /** Map of plannedPayment.id -> sum of all linked transaction amounts */
  const commitmentPaidMap = useMemo(() => {
    const map = new Map<string, number>();
    transactions.forEach(tx => {
      if (tx.linkedId?.startsWith('planned_')) {
        const ppId = tx.linkedId.replace('planned_', '');
        map.set(ppId, (map.get(ppId) || 0) + tx.amount);
      }
    });
    return map;
  }, [transactions]);

  const categoryTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    filteredTransactions.forEach(tx => {
      const cat = tx.type === 'spend' ? 'Spend' : (tx.category || 'Uncategorized');
      if (!totals[cat]) totals[cat] = 0;
      totals[cat] += tx.amount;
    });
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [filteredTransactions]);
  
  const categoryData = useMemo(() => {
    return categoryTotals.map(([name, value]) => ({ name, value }));
  }, [categoryTotals]);
  
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);
  const [debtModalInitialType, setDebtModalInitialType] = useState<'loan' | 'credit_card' | 'mortgage' | 'line_of_credit'>('loan');
  const [isBankAccountModalOpen, setIsBankAccountModalOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<import('../types').BankAccount | null>(null);
  const [feeModalAccount, setFeeModalAccount] = useState<import('../types').BankAccount | null>(null);
  const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isWishlistModalOpen, setIsWishlistModalOpen] = useState(false);
  const [editingWishlistItem, setEditingWishlistItem] = useState<import('../types').WishlistItem | null>(null);
  
  const [isPlannedPaymentModalOpen, setIsPlannedPaymentModalOpen] = useState(false);
  const [editingPlannedPayment, setEditingPlannedPayment] = useState<import('../types').PlannedPayment | null>(null);
  
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [editingIncome, setEditingIncome] = useState<IncomeStream | null>(null);
  const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);
  
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const [expandedBankAccounts, setExpandedBankAccounts] = useState<Set<string>>(new Set());
  const [expandedCreditCards, setExpandedCreditCards] = useState<Set<string>>(new Set());
  
  const [plaidMappingData, setPlaidMappingData] = useState<{ itemId: string, institutionName: string, accounts: any[] } | null>(null);
  const [syncingPlaid, setSyncingPlaid] = useState(false);
  const [syncingBalances, setSyncingBalances] = useState(false);
  const [syncingTransactions, setSyncingTransactions] = useState(false);

  const [plaidItems, setPlaidItems] = useState<PlaidItemDoc[]>([]);
  useEffect(() => {
    if (!householdId) return;
    // plaid_connections, not plaid_items -- plaid_items holds the real
    // Plaid access token and is deliberately never client-readable (see
    // firestore.rules); plaid_connections is the safe mirror the Cloud
    // Functions keep in sync with it.
    const unsub = onSnapshot(
      query(collection(db, 'plaid_connections'), where('householdId', '==', householdId)),
      (snapshot) => {
        setPlaidItems(snapshot.docs.map(d => ({
          id: d.id,
          institutionName: d.data().institutionName || 'Unknown Bank',
          needsReconnect: !!d.data().needsReconnect,
          lastSyncAt: d.data().lastSyncAt?.toDate ? d.data().lastSyncAt.toDate() : null,
          lastSyncOk: d.data().lastSyncOk ?? null,
          lastSyncMessage: d.data().lastSyncMessage ?? null,
        })));
      },
      (error) => console.error('Error loading plaid_connections:', error)
    );
    return () => unsub();
  }, [householdId]);

  // currentBalance/currentAmount is a pure running tally, never a real
  // number synced from Plaid -- see plaidSyncBalances in functions/src/
  // plaid.ts for the full explanation. This is the one-click fix for when
  // it's drifted from reality (importing a big batch of historical Plaid
  // transactions on top of a near-$0 initialBalance is the usual cause).
  const handleSyncRealBalances = async () => {
    setSyncingBalances(true);
    try {
      // The client SDK's own default timeout (70s) is shorter than the
      // 180s this function is actually configured to allow server-side --
      // without overriding it here, a real multi-institution sync that
      // takes over 70s (one slow/erroring institution is enough) shows a
      // misleading "deadline-exceeded" failure even when it goes on to
      // succeed anyway once the client's stopped listening.
      const syncFn = httpsCallable(functions, 'plaidSyncBalances', { timeout: 170000 });
      const result: any = await syncFn({});
      const corrections = result.data?.corrections || [];
      const itemErrors = result.data?.itemErrors || [];

      const nameFor = (c: any) => {
        const list = c.accountType === 'bank_account' ? bankAccounts : debts;
        return list.find((a: any) => a.id === c.accountId)?.name || c.accountId.slice(0, 6);
      };

      const parts: string[] = [];
      if (corrections.length > 0) {
        const lines = corrections.map((c: any) => `${nameFor(c)}: $${c.oldValue.toFixed(2)} → $${c.realValue.toFixed(2)}`).join('\n');
        parts.push(`Corrected ${corrections.length} account(s):\n${lines}`);
      } else {
        parts.push('Every reachable linked account already matches its real Plaid balance.');
      }
      if (itemErrors.length > 0) {
        const lines = itemErrors.map((e: any) => `${e.institutionName}: ${e.message}`).join('\n');
        parts.push(`Could not check ${itemErrors.length} institution(s):\n${lines}`);
      }
      alert(parts.join('\n\n'));
    } catch (err: any) {
      console.error(err);
      alert('Failed to sync real balances: ' + err.message);
    } finally {
      setSyncingBalances(false);
    }
  };

  // Previously there was no way to pull *new* transactions for a bank
  // you'd already linked -- plaidSyncTransactions only ever ran once,
  // right after the initial account-mapping step. plaidSyncAllTransactions
  // reconstructs that mapping from what's already persisted on each
  // account instead of asking for it again, so this can run any time.
  const handleSyncNow = async () => {
    setSyncingTransactions(true);
    try {
      // Same client/server timeout mismatch fix as handleSyncRealBalances above.
      const syncFn = httpsCallable(functions, 'plaidSyncAllTransactions', { timeout: 170000 });
      const result: any = await syncFn({});
      const results = result.data?.results || [];
      if (results.length === 0) {
        alert('No linked bank accounts to sync yet -- connect one first.');
      } else {
        const lines = results.map((r: any) => {
          if (r.needsReconnect) return `${r.institutionName}: needs reconnecting`;
          if (r.error) return `${r.institutionName}: ${r.error}`;
          return `${r.institutionName}: ${r.added} new transaction(s)`;
        }).join('\n');
        const totalAdded = results.reduce((sum: number, r: any) => sum + (r.added || 0), 0);
        alert(`${lines}${totalAdded > 0 ? '\n\nReview and approve them on Reconcile Statements.' : ''}`);
      }
    } catch (err: any) {
      console.error(err);
      alert('Failed to sync transactions: ' + err.message);
    } finally {
      setSyncingTransactions(false);
    }
  };

  // Bank Financials tab: each top-level section (Bank Accounts, Lines of
  // Credit, Credit Cards) is its own full-width collapsible "cabinet",
  // separate from the per-account/per-card transaction-list expansion
  // below. All start open so nothing changes for someone who's never
  // touched a header.
  const [expandedFinancialSections, setExpandedFinancialSections] = useState<Set<string>>(
    new Set(['bank_accounts', 'lines_of_credit', 'credit_cards', 'expected_income', 'actual_income', 'recurring_expenses', 'debts_overview'])
  );
  const toggleFinancialSection = (key: string) => {
    setExpandedFinancialSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handlePlaidSaveMappings = async (mappings: Record<string, { id: string; type: 'bank_account' | 'debt' }>) => {
    if (!plaidMappingData) return;
    try {
      const syncFn = httpsCallable(functions, 'plaidSyncTransactions');
      await syncFn({
        itemId: plaidMappingData.itemId,
        householdId,
        manualMappings: mappings
      });
      setPlaidMappingData(null);
      setCurrentTab('reconcile'); // Send them to the reconcile dashboard!
    } catch (err: any) {
      console.error(err);
      alert('Failed to sync transactions: ' + err.message);
    }
  };

  // "Manage Accounts" -- lets a household map an already-connected bank's
  // accounts that were skipped (or are new since) the original Connect
  // Bank flow, without ever opening Plaid Link again. Reuses the same
  // PlaidAccountMapper UI as the first-connect flow -- it already takes
  // the full household bankAccounts/debts lists, unfiltered, so an account
  // created via the normal Add Bank Account / Add Debt button shows up as
  // a mappable target here with zero changes to that component.
  const [manageAccountsData, setManageAccountsData] = useState<{ itemId: string, institutionName: string, accounts: any[] } | null>(null);
  const [managingAccountsFor, setManagingAccountsFor] = useState<string | null>(null);

  const handleManageAccounts = async (itemId: string, institutionName: string) => {
    setManagingAccountsFor(itemId);
    try {
      const listFn = httpsCallable(functions, 'plaidGetAccountsForItem');
      const result: any = await listFn({ itemId, householdId });
      const plaidAccounts: any[] = result.data?.accounts || [];

      const alreadyMapped = new Set<string>();
      bankAccounts.forEach((a: any) => { if (a.plaidItemId === itemId && a.plaidAccountId) alreadyMapped.add(a.plaidAccountId); });
      debts.forEach((d: any) => { if (d.plaidItemId === itemId && d.plaidAccountId) alreadyMapped.add(d.plaidAccountId); });

      const unmapped = plaidAccounts.filter(a => !alreadyMapped.has(a.id));
      if (unmapped.length === 0) {
        alert(`Every account Plaid reports at ${institutionName} is already mapped. If you're missing one, it may need to be re-shared via Reconnect first (some banks restrict which accounts they hand over until you re-consent).`);
        return;
      }
      setManageAccountsData({ itemId, institutionName, accounts: unmapped });
    } catch (err: any) {
      console.error(err);
      alert(`Failed to list ${institutionName}'s accounts: ` + (err.message || err));
    } finally {
      setManagingAccountsFor(null);
    }
  };

  const handleSaveNewMappings = async (mappings: Record<string, { id: string; type: 'bank_account' | 'debt' }>) => {
    if (!manageAccountsData) return;
    try {
      const addFn = httpsCallable(functions, 'plaidAddAccountMappings', { timeout: 170000 });
      const result: any = await addFn({
        itemId: manageAccountsData.itemId,
        householdId,
        manualMappings: mappings
      });
      const added = result.data?.added || 0;
      setManageAccountsData(null);
      if (added > 0) {
        alert(`Mapped ${Object.keys(mappings).length} account(s) and found ${added} real transaction(s) for them. Review and approve them on Reconcile Statements.`);
        setCurrentTab('reconcile');
      } else {
        alert(`Mapped ${Object.keys(mappings).length} account(s). No transaction history found for them yet.`);
      }
    } catch (err: any) {
      console.error(err);
      alert('Failed to map accounts: ' + (err.message || err));
    }
  };

  const toggleBankAccount = (id: string) => {
    setExpandedBankAccounts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCreditCard = (id: string) => {
    setExpandedCreditCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteDebt = async (id: string) => {
    if (confirm('Are you sure you want to delete this debt? This cannot be undone.')) {
      await deleteDoc(doc(db, 'debts', id));
    }
  };

  const handleDeleteIncome = async (id: string) => {
    if (confirm('Are you sure you want to delete this income source?')) {
      await deleteDoc(doc(db, 'incomes', id));
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (confirm('Are you sure you want to delete this recurring expense?')) {
      await deleteDoc(doc(db, 'recurring_expenses', id));
    }
  };

  const handleExportBudgetCSV = () => {
    const rows: (string | number)[][] = [];

    rows.push(['Recurring Expenses']);
    rows.push(['Name', 'Category', 'Amount', 'Frequency', 'Notes', 'Created']);
    recurringExpenses.forEach(exp => {
      rows.push([
        exp.name || exp.category,
        exp.category,
        exp.amount.toFixed(2),
        formatSchedule(exp.schedule),
        exp.notes || '',
        exp.createdAt ? format(exp.createdAt, 'yyyy-MM-dd') : '',
      ]);
    });

    rows.push([]);
    rows.push(['Debts']);
    rows.push(['Name', 'Type', 'Current Balance', 'Total Amount', 'Monthly Minimum', 'Interest Rate (%)', 'Frequency', 'Created']);
    debts.forEach(debt => {
      rows.push([
        debt.name,
        debt.type || 'loan',
        debt.currentAmount.toFixed(2),
        debt.totalAmount.toFixed(2),
        debt.monthlyMinimum.toFixed(2),
        debt.interestRate,
        formatSchedule(debt.schedule),
        debt.createdAt ? format(debt.createdAt, 'yyyy-MM-dd') : '',
      ]);
    });

    downloadCSV(`budget-and-debts-${format(new Date(), 'yyyy-MM-dd')}.csv`, rows);
  };

  const handleExportIncomeCSV = () => {
    const rows: (string | number)[][] = [
      ['Source', 'Amount', 'Frequency', 'Linked Bank Account', 'Created'],
    ];
    incomes.forEach(inc => {
      const linkedAccount = bankAccounts.find(a => a.id === inc.bankAccountId);
      rows.push([
        inc.source,
        inc.amount.toFixed(2),
        formatSchedule(inc.schedule),
        linkedAccount ? linkedAccount.name : '',
        inc.createdAt ? format(inc.createdAt, 'yyyy-MM-dd') : '',
      ]);
    });

    downloadCSV(`income-${format(new Date(), 'yyyy-MM-dd')}.csv`, rows);
  };
  const { income, fixedExpense, dailySpend, debtPaid, savings, balance } = useMemo(() => {
    let inc = 0;
    let exp = 0;
    let spd = 0;
    let debt = 0;
    let sav = 0;
    let cashExp = 0;
    let cashSpd = 0;
    const now = new Date();

    transactions.forEach(tx => {
      // A "Balance Reconciliation" correction (from Sync Real Balances)
      // isn't a real economic event -- it's just catching the tracked
      // balance up to Plaid's real one, and the gap is just as often an
      // untracked transfer between the household's own accounts/lines of
      // credit as it is real income/spending. Counting it here would
      // silently inflate whichever bucket the correction's sign happened
      // to land in. The raw transaction still exists and is fully visible
      // in the ledger -- this only excludes it from this summary.
      if (tx.category === 'Balance Reconciliation') return;
      if (tx.date && isSameMonth(tx.date, now)) {
        if (tx.type === 'income') inc += tx.amount;
        if (tx.type === 'expense') {
          exp += tx.amount;
          if (tx.paymentMethod !== 'credit') cashExp += tx.amount;
        }
        if (tx.type === 'spend') {
          spd += tx.amount;
          if (tx.paymentMethod !== 'credit') cashSpd += tx.amount;
        }
        if (tx.type === 'debt_payment') debt += tx.amount;
        if (tx.type === 'savings_contribution') sav += tx.amount;
      }
    });

    return { 
      income: inc, 
      fixedExpense: exp, 
      dailySpend: spd, 
      debtPaid: debt,
      savings: sav,
      balance: inc - cashExp - cashSpd - debt - sav 
    };
  }, [transactions]);

  const { totalCreditLimit, totalCreditBalance, totalBankBalance, totalInvestmentBalance, totalLocLimit, totalLocBalance } = useMemo(() => {
    const totalCreditLimit = debts.filter(d => d.type === 'credit_card').reduce((sum, d) => sum + (d.totalAmount || 0), 0);
    const totalCreditBalance = debts.filter(d => d.type === 'credit_card').reduce((sum, d) => sum + (d.currentAmount || 0), 0);
    const totalLocLimit = debts.filter(d => d.type === 'line_of_credit').reduce((sum, d) => sum + (d.totalAmount || 0), 0);
    const totalLocBalance = debts.filter(d => d.type === 'line_of_credit').reduce((sum, d) => sum + (d.currentAmount || 0), 0);
    // Liquid cash only -- chequing/savings/joint. Investment accounts
    // (RRSP/TFSA/RESP/margin/other) are real money but not spendable cash,
    // so they're kept out of this figure and totalled separately instead;
    // otherwise "bank balance" reads as available cash when part of it
    // actually isn't.
    const totalBankBalance = bankAccounts.filter(b => !b.type.startsWith('investment_')).reduce((sum, b) => sum + (b.currentBalance || 0), 0);
    const totalInvestmentBalance = bankAccounts.filter(b => b.type.startsWith('investment_')).reduce((sum, b) => sum + (b.currentBalance || 0), 0);
    return { totalCreditLimit, totalCreditBalance, totalBankBalance, totalInvestmentBalance, totalLocLimit, totalLocBalance };
  }, [debts, bankAccounts]);

  // Bank Accounts section groups accounts by kind instead of one mixed
  // list -- joint accounts are pulled out into their own group regardless
  // of whether they're chequing or savings, since that's how a household
  // actually thinks about "our account" vs. "my account".
  const bankAccountGroups = useMemo(() => {
    const chequing = bankAccounts.filter(a => a.type === 'checking' && !a.isJoint);
    const savings = bankAccounts.filter(a => a.type === 'savings' && !a.isJoint);
    const joint = bankAccounts.filter(a => a.isJoint);
    const investments = bankAccounts.filter(a => a.type.startsWith('investment_') && !a.isJoint);
    return [
      { key: 'chequing', label: 'Chequing', icon: Wallet, accounts: chequing },
      { key: 'savings', label: 'Savings', icon: PiggyBank, accounts: savings },
      { key: 'joint', label: 'Joint Accounts', icon: Users, accounts: joint },
      { key: 'investments', label: 'Investments', icon: LineChart, accounts: investments },
    ].filter(g => g.accounts.length > 0);
  }, [bankAccounts]);
  
  const creditCardUtilization = totalCreditLimit > 0 ? (totalCreditBalance / totalCreditLimit) * 100 : 0;

  // Debts & Budget tab: same grouping treatment as Bank Accounts -- loans,
  // mortgages, credit cards and lines of credit stop being one mixed list.
  const debtGroups = useMemo(() => {
    const loans = debts.filter(d => d.type === 'loan' || !d.type);
    const mortgages = debts.filter(d => d.type === 'mortgage');
    const creditCards = debts.filter(d => d.type === 'credit_card');
    const locs = debts.filter(d => d.type === 'line_of_credit');
    return [
      { key: 'loans', label: 'Loans', icon: Banknote, items: loans, revolving: false },
      { key: 'mortgages', label: 'Mortgages', icon: Home, items: mortgages, revolving: false },
      { key: 'credit_cards', label: 'Credit Cards', icon: CreditCard, items: creditCards, revolving: true },
      { key: 'lines_of_credit', label: 'Lines of Credit', icon: CreditCard, items: locs, revolving: true },
    ].filter(g => g.items.length > 0);
  }, [debts]);

  const { totalDebtAmount, totalDebtBalance } = useMemo(() => {
    const totalDebtAmount = debts.reduce((sum, d) => sum + (d.totalAmount || 0), 0);
    const totalDebtBalance = debts.reduce((sum, d) => sum + (d.currentAmount || 0), 0);
    return { totalDebtAmount, totalDebtBalance };
  }, [debts]);

  // Normalized to a monthly figure regardless of each income's own
  // frequency, so streams on different schedules (weekly, bi-weekly,
  // semi-monthly, monthly) can be summed into one meaningful total --
  // mirrors ExpectedIncome.tsx's own per-item yearly estimate.
  const expectedIncomeMonthlyTotal = useMemo(() => {
    return incomes.reduce((sum, inc) => {
      if (!inc.schedule) return sum + inc.amount; // legacy fallback
      let perMonth = inc.amount;
      if (inc.schedule.frequency === 'semi-monthly') perMonth = inc.amount * 2;
      else if (inc.schedule.frequency === 'bi-weekly') perMonth = (inc.amount * 26) / 12;
      else if (inc.schedule.frequency === 'weekly') perMonth = (inc.amount * 52) / 12;
      return sum + perMonth;
    }, 0);
  }, [incomes]);

  const ytdIncomeTxs = useMemo(() => {
    const year = new Date().getFullYear();
    // Excludes Balance Reconciliation corrections -- see the comment on
    // the "This Month" summary above for why those aren't real income.
    return transactions
      .filter(t => t.type === 'income' && t.category !== 'Balance Reconciliation' && t.date && t.date.getFullYear() === year)
      .sort((a, b) => b.date!.getTime() - a.date!.getTime());
  }, [transactions]);
  const ytdIncomeTotal = useMemo(() => ytdIncomeTxs.reduce((sum, t) => sum + t.amount, 0), [ytdIncomeTxs]);

  const recurringExpenseTotals = useMemo(() => {
    const now = new Date();
    let expected = 0;
    let actual = 0;
    recurringExpenses.forEach(exp => {
      expected += exp.amount;
      actual += transactions
        .filter(t =>
          t.type === 'expense' &&
          t.date && isSameMonth(t.date, now) &&
          (t.linkedId === exp.id || t.category.toLowerCase() === exp.category.toLowerCase())
        )
        .reduce((sum, t) => sum + t.amount, 0);
    });
    return { expected, actual };
  }, [recurringExpenses, transactions]);

  const handleDeleteTransaction = async (id?: string) => {
    if (!id) return;
    const tx = transactions.find(t => t.id === id);
    if (!tx) return;

    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        // Reverse balances
        if (tx.linkedId) {
          if (tx.type === 'savings_contribution') {
            await updateDoc(doc(db, 'goals', tx.linkedId), {
              currentAmount: increment(-tx.amount)
            });
          } else if (tx.type === 'debt_payment') {
            await updateDoc(doc(db, 'debts', tx.linkedId), {
              currentAmount: increment(tx.amount)
            });
          }
        }
        
        // Use tx.linkedId (stored on every transaction) to find the planned payment
        let linkedPlanned = plannedPayments.find(p => p.linkedTransactionId === tx.id);
        if (!linkedPlanned && tx.linkedId?.startsWith('planned_')) {
          const ppId = tx.linkedId.replace('planned_', '');
          linkedPlanned = plannedPayments.find(p => p.id === ppId);
        }
        if (linkedPlanned && linkedPlanned.id) {
          const newPaidAmount = Math.max(0, (linkedPlanned.paidAmount || 0) - tx.amount);
          const isStillFullyPaid = newPaidAmount >= linkedPlanned.amount;
          await updateDoc(doc(db, 'planned_payments', linkedPlanned.id), {
            paidAmount: newPaidAmount,
            status: isStillFullyPaid ? 'paid' : 'pending',
            manuallyCompleted: isStillFullyPaid ? (linkedPlanned.manuallyCompleted || false) : false,
            linkedTransactionId: null
          });
        }

        if (tx.paymentMethod === 'credit' && tx.creditCardId) {
          await updateDoc(doc(db, 'debts', tx.creditCardId), {
            currentAmount: increment(-tx.amount)
          });
        }
        
        if (tx.bankAccountId) {
          if (tx.type === 'transfer') {
            await updateDoc(doc(db, 'bank_accounts', tx.bankAccountId), {
              currentBalance: increment(tx.amount)
            });
          } else {
            const isIncome = tx.type === 'income';
            await updateDoc(doc(db, 'bank_accounts', tx.bankAccountId), {
              currentBalance: increment(isIncome ? -tx.amount : tx.amount)
            });
          }
        }

        if (tx.type === 'transfer' && tx.toBankAccountId) {
           await updateDoc(doc(db, 'bank_accounts', tx.toBankAccountId), {
             currentBalance: increment(-tx.amount)
           });
        }

        await deleteDoc(doc(db, 'transactions', id));
      } catch (err) {
        console.error("Failed to delete transaction", err);
      }
    }
  };

  const handleDeleteWishlistItem = async (id?: string) => {
    if (!id) return;
    if (window.confirm("Are you sure you want to delete this wishlist item?")) {
      try {
        await deleteDoc(doc(db, 'wishlist', id));
      } catch (err) {
        console.error("Failed to delete wishlist item", err);
      }
    }
  };

  const handleSignOut = () => signOut(auth);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
      <aside className="w-64 bg-slate-900 hidden md:flex flex-col shrink-0">
        <div className="p-6">
          <div className="flex items-center gap-3 text-white mb-8">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center font-bold text-xl">B</div>
            <span className="text-lg font-bold tracking-tight">Tabulario</span>
          </div>
          <nav className="space-y-1">
            <button 
              onClick={() => setCurrentTab('overview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'overview' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <Wallet className="w-4 h-4" /> Overview
            </button>
            <button 
              onClick={() => setCurrentTab('debts_budget')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'debts_budget' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <CreditCard className="w-4 h-4" /> Debts & Budget
            </button>
            <button 
              onClick={() => setCurrentTab('bank_financials')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'bank_financials' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <Banknote className="w-4 h-4" /> Bank Financials
            </button>
            <button
              onClick={() => setCurrentTab('reconcile')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'reconcile' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <UploadCloud className="w-4 h-4" /> Reconcile Statements
            </button>
            <button
              onClick={() => setCurrentTab('net_worth')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'net_worth' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <Scale className="w-4 h-4" /> Net Worth
            </button>
            <button
              onClick={() => setCurrentTab('ai_strategy')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${currentTab === 'ai_strategy' ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              <Bot className="w-5 h-5 text-slate-400" />
              AI Hub
            </button>
            <button 
              onClick={() => setCurrentTab('vault')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'vault' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <MessageSquare className="w-4 h-4" /> Vault
            </button>
            <button 
              onClick={() => setCurrentTab('goals_planner')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'goals_planner' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <Target className="w-4 h-4" /> Goals Planner
            </button>
            <button 
              onClick={() => setCurrentTab('planning_schedule')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'planning_schedule' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <Calendar className="w-4 h-4" /> Planning Schedule
            </button>
            <button 
              onClick={() => setCurrentTab('debt_crush')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'debt_crush' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <TrendingDown className="w-4 h-4" /> Debt Crush
            </button>
            <button 
              onClick={() => setCurrentTab('settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${currentTab === 'settings' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              <SettingsIcon className="w-4 h-4" /> Settings
            </button>
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold">{user.email ? user.email.charAt(0).toUpperCase() : 'U'}</div>
            <div className="truncate">
              <p className="text-sm font-semibold text-white truncate">{user.email || 'User'}</p>
              <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Premium Member</p>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col p-4 md:p-8 gap-6 overflow-y-auto min-h-0 bg-slate-100 pb-24 md:pb-8">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex justify-between w-full sm:w-auto items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 capitalize">
                {currentTab === 'debts_budget' ? 'Debts & Budget' : currentTab === 'ai_strategy' ? 'AI Strategy' : currentTab === 'bank_financials' ? 'Bank Financials' : currentTab === 'goals_planner' ? 'Goals Planner' : currentTab === 'planning_schedule' ? 'Planning Schedule' : currentTab === 'net_worth' ? 'Net Worth' : currentTab === 'debt_crush' ? 'Debt Crush' : currentTab === 'reconcile' ? 'Reconcile Statements' : 'Overview'}
              </h1>
              <p className="text-slate-500 text-sm italic">Performance for {format(new Date(), 'MMMM yyyy')}</p>
            </div>
            <button 
              onClick={handleSignOut}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors sm:hidden"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {currentTab === 'overview' && (
              <>
                <button 
                  onClick={() => { setEditingWishlistItem(null); setIsWishlistModalOpen(true); }}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-white border border-slate-200 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700"
                >
                  <Heart className="w-4 h-4" /> <span className="hidden sm:inline">Add Wish</span>
                </button>
                <button 
                  onClick={() => { setEditingGoal(null); setIsGoalModalOpen(true); }}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-white border border-slate-200 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700"
                >
                  <Target className="w-4 h-4" /> <span className="hidden sm:inline">Add Goal</span>
                </button>
                <button 
                  onClick={() => { setEditingTransaction(null); setIsTxModalOpen(true); }}
                  className="flex-[2] sm:flex-none justify-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Transaction
                </button>
              </>
            )}
            {currentTab === 'bank_financials' && (
              <>
                <button
                  onClick={() => { setEditingBankAccount(null); setIsBankAccountModalOpen(true); }}
                  className="flex-1 sm:flex-none justify-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Bank Account
                </button>
                <div className="flex-1 sm:flex-none">
                  <PlaidConnectButton
                    householdId={householdId}
                    onSuccess={(itemId, institutionName, accounts) => setPlaidMappingData({ itemId, institutionName, accounts })}
                  />
                </div>
                <button
                  onClick={handleSyncNow}
                  disabled={syncingTransactions}
                  title="Pull new transactions for every already-linked bank -- nothing syncs automatically otherwise"
                  className="flex-1 sm:flex-none justify-center px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Download className={`w-4 h-4 ${syncingTransactions ? 'animate-bounce' : ''}`} />
                  {syncingTransactions ? 'Syncing…' : 'Sync Now'}
                </button>
                <button
                  onClick={handleSyncRealBalances}
                  disabled={syncingBalances}
                  title="Correct every Plaid-linked account's balance to match what your bank actually reports right now"
                  className="flex-1 sm:flex-none justify-center px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncingBalances ? 'animate-spin' : ''}`} />
                  {syncingBalances ? 'Syncing…' : 'Sync Real Balances'}
                </button>
                <button
                  onClick={() => setIsCreditCardModalOpen(true)}
                  className="flex-1 sm:flex-none justify-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold shadow-sm hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Credit Card
                </button>
                <button 
                  onClick={() => { setEditingDebt(null); setDebtModalInitialType('line_of_credit'); setIsDebtModalOpen(true); }}
                  className="flex-1 sm:flex-none justify-center px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-semibold shadow-sm hover:bg-emerald-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Line of Credit
                </button>
              </>
            )}
            
            {currentTab === 'debts_budget' && (
              <>
                <button 
                  onClick={() => setIsBudgetModalOpen(true)}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-emerald-600 text-white border border-emerald-700 rounded-md text-sm font-semibold shadow-sm hover:bg-emerald-700 transition-colors flex items-center gap-2"
                >
                  <Banknote className="w-4 h-4" /> <span className="hidden sm:inline">Add Budget Item</span>
                </button>
                <button
                  onClick={() => setIsDebtModalOpen(true)}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-rose-600 text-white rounded-md text-sm font-semibold shadow-sm hover:bg-rose-700 transition-colors flex items-center gap-2"
                >
                  <CreditCard className="w-4 h-4" /> <span className="hidden sm:inline">Add Debt</span>
                </button>
                <button
                  onClick={handleExportBudgetCSV}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2"
                  title="Export recurring expenses and debts as one CSV"
                >
                  <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export Budget CSV</span>
                </button>
                <button
                  onClick={handleExportIncomeCSV}
                  className="flex-1 sm:flex-none justify-center px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-md text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2"
                  title="Export income sources as a separate CSV"
                >
                  <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export Income CSV</span>
                </button>
              </>
            )}

            <button 
              onClick={handleSignOut}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors ml-2 hidden sm:block"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {currentTab === 'overview' && (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Remaining Cash Flow
                  <InfoTooltip iconClassName="text-slate-500">
                    What's left in cash after this month's real spending: income minus fixed expenses, daily spending, debt payments, and savings contributions. Only counts the parts paid in cash — a purchase you put on a credit card doesn't leave your bank account yet, so it's not subtracted here.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-white leading-none">
                  ${balance.toFixed(2)}
                </p>
                <p className="text-[9px] text-slate-400 mt-1.5 font-medium">Income - All Outflows</p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-indigo-200 shadow-sm bg-indigo-50/30">
                <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mb-1">
                  Liquid Cash
                  <InfoTooltip iconClassName="text-indigo-400">
                    The combined balance of your chequing, savings, and joint accounts right now — money you could spend today. Investment accounts (RRSP, TFSA, RESP, etc.) are left out since that money isn't readily spendable.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${totalBankBalance.toFixed(2)}
                </p>
                <p className="text-[9px] text-slate-400 mt-1.5 font-medium">Chequing, savings &amp; joint only</p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-violet-200 shadow-sm bg-violet-50/30">
                <p className="text-[10px] font-bold text-violet-600 uppercase tracking-widest mb-1">
                  Available LOC
                  <InfoTooltip iconClassName="text-violet-400">
                    How much more you could draw today: the total limit across every Line of Credit you track, minus what you currently owe on them.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${(totalLocLimit - totalLocBalance).toFixed(2)}
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-pink-200 shadow-sm bg-pink-50/30">
                <p className="text-[10px] font-bold text-pink-600 uppercase tracking-widest mb-1">
                  CC Utilization
                  <InfoTooltip iconClassName="text-pink-400">
                    All your credit card balances added up, divided by all your credit card limits added up, shown as a percentage. Lenders and credit scores generally like to see this under 30%.
                  </InfoTooltip>
                </p>
                <div className="flex items-end gap-2">
                  <p className="text-lg font-bold text-slate-900 leading-none">
                    {creditCardUtilization.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-emerald-200 shadow-sm bg-emerald-50/30">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1">
                  Income
                  <InfoTooltip iconClassName="text-emerald-400">
                    Every income transaction you've logged so far this calendar month, added together. Resets back to $0 at the start of each new month.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${income.toFixed(2)}
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-rose-200 shadow-sm bg-rose-50/30">
                <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest mb-1">
                  Fixed Expenses
                  <InfoTooltip iconClassName="text-rose-400">
                    Your recurring bills and obligations logged so far this calendar month — rent, subscriptions, and the like. Kept separate from Daily Spend, which is your day-to-day discretionary purchases.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${fixedExpense.toFixed(2)}
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-amber-200 shadow-sm bg-amber-50/30">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">
                  Daily Spend
                  <InfoTooltip iconClassName="text-amber-400">
                    Your day-to-day discretionary purchases logged so far this calendar month — groceries, coffee, shopping. Kept separate from Fixed Expenses, which is your recurring bills.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${dailySpend.toFixed(2)}
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm bg-blue-50/30">
                <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">
                  Debt & Savings
                  <InfoTooltip iconClassName="text-blue-400">
                    This calendar month's debt payments plus this month's savings contributions, added together into one number — the total you've put toward your future this month, whether that's paying down what you owe or building up what you keep.
                  </InfoTooltip>
                </p>
                <p className="text-lg font-bold text-slate-900 leading-none">
                  ${(debtPaid + savings).toFixed(2)}
                </p>
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 flex flex-col gap-6">
                <Charts transactions={transactions} />
                <ProjectionChart transactions={transactions} debts={debts} goals={goals} />
                <StressTester bankAccounts={bankAccounts} debts={debts} expenses={recurringExpenses} />
              </div>
              
              <div className="flex flex-col gap-4">
                <div className="bg-slate-900 text-white p-6 rounded-xl shadow-lg flex-shrink-0">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Long-Term Goals</h3>
                  {goals.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No goals set.</p>
                  ) : (
                    <div className="space-y-4">
                      {goals.map(goal => {
                        const progress = Math.min(100, Math.round((goal.currentAmount / goal.targetAmount) * 100));
                        const isDebt = goal.type === 'debt';
                        return (
                          <div key={goal.id} className="group cursor-pointer" onClick={() => { setEditingGoal(goal); setIsGoalModalOpen(true); }}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="group-hover:text-blue-400 transition-colors flex items-center gap-1">{goal.name} <span className="opacity-0 group-hover:opacity-100 text-[10px] bg-slate-800 px-1 rounded ml-1">Edit</span></span>
                              <span className="font-bold">{progress}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mb-1">
                              <div 
                                className={`h-full ${isDebt ? 'bg-rose-400' : 'bg-blue-400'}`} 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400">
                              <span>${goal.currentAmount.toFixed(0)}</span>
                              <span>${goal.targetAmount.toFixed(0)}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex-1 overflow-y-auto min-h-[200px]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Family Wishlist</h3>
                    <Heart className="w-4 h-4 text-rose-400" />
                  </div>
                  {wishlist.length === 0 ? (
                    <p className="text-xs text-slate-500 italic text-center mt-8">What do you want to save for next? Add to your wishlist!</p>
                  ) : (
                    <div className="space-y-3">
                      {wishlist.map(item => (
                        <div key={item.id} className="p-3 border border-slate-100 rounded-lg bg-slate-50 group cursor-pointer" onClick={() => { setEditingWishlistItem(item); setIsWishlistModalOpen(true); }}>
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-sm font-bold text-slate-800 group-hover:text-blue-600 transition-colors flex items-center gap-2">
                              {item.name}
                              <span className="opacity-0 group-hover:opacity-100 text-[10px] bg-slate-200 px-1 rounded text-slate-600">Edit</span>
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-bold text-slate-900">${item.estimatedCost}</span>
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDeleteWishlistItem(item.id); }}
                                className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between items-center text-[10px]">
                            <span className={`px-1.5 py-0.5 rounded uppercase font-bold tracking-wider ${
                              item.priorityLevel === 'High' ? 'bg-rose-100 text-rose-700' :
                              item.priorityLevel === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {item.priorityLevel} Priority
                            </span>
                            {item.targetDate && (
                              <span className="text-slate-500">Target: {format(item.targetDate, 'MMM yyyy')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-shrink-0">
              <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <h3 className="font-bold text-slate-800">Recent Transactions</h3>
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-2 py-1 rounded">{filteredTransactions.length} Total</span>
                  <button 
                    onClick={async () => {
                      if (window.confirm("This will reset the WealthSimple balance to $307.11. Proceed?")) {
                        const targetAccount = bankAccounts.find(b => String(b.name).toLowerCase().includes('wealthsimple'));
                        if (!targetAccount) {
                          alert("Could not find an account named 'WealthSimple'");
                          return;
                        }
                        try {
                          const { doc, updateDoc } = await import('firebase/firestore');
                          const { db } = await import('../lib/firebase');
                          await updateDoc(doc(db, 'bank_accounts', targetAccount.id), {
                            currentBalance: 307.11
                          });
                          alert("Successfully reset the balance to $307.11!");
                        } catch (e) {
                          console.error(e);
                          alert("Failed to reset balance.");
                        }
                      }
                    }}
                    className="text-xs font-bold text-white uppercase tracking-wider bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded transition-colors"
                  >
                    Force Balance Reset
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {genericCategoryTransactions.length > 0 && (
                    <button
                      onClick={handleSuggestTransactionCategories}
                      disabled={suggestingCategories}
                      title="Ask AI to suggest a real category for transactions still sitting on a generic imported category"
                      className="flex items-center gap-1.5 bg-white text-slate-600 border border-slate-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {suggestingCategories ? 'Suggesting…' : `Suggest categories (${genericCategoryTransactions.length})`}
                    </button>
                  )}
                  <select
                    value={dateFilter}
                    onChange={(e) => {
                      setDateFilter(e.target.value as any);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Time</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                    <option value="6m">Last 6 Months</option>
                    <option value="1y">Last 1 Year</option>
                  </select>
                  <select 
                    value={transactionFilter} 
                    onChange={(e) => {
                      setTransactionFilter(e.target.value as any);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Transactions</option>
                    <option value="income">Income</option>
                    <option value="spend">Ad-Hoc Spends</option>
                    <option value="expense">Fixed Expenses</option>
                    <option value="debt_payment">Debt Payments</option>
                    <option value="savings_contribution">Savings</option>
                    <option value="transfer">Transfers</option>
                  </select>
                </div>
              </div>

              {categorySuggestError && (
                <p className="px-6 py-2 text-xs text-rose-600 bg-rose-50 border-b border-rose-100">{categorySuggestError}</p>
              )}

              {pendingCategorySuggestions && pendingCategorySuggestions.length === 0 && (
                <p className="px-6 py-3 text-xs text-slate-500 bg-slate-50/50 border-b border-slate-100">
                  No better category found for these transactions.
                  <button onClick={() => setPendingCategorySuggestions(null)} className="ml-2 text-blue-600 font-semibold hover:underline">Dismiss</button>
                </p>
              )}

              {pendingCategorySuggestions && pendingCategorySuggestions.length > 0 && (
                <div className="px-6 py-4 bg-blue-50/40 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Suggested Categories -- Review &amp; Apply</h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPendingCategorySuggestions(null)}
                        className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApplyCategorySuggestions}
                        disabled={applyingSuggestions || Object.values(acceptedSuggestionIds).every(v => !v)}
                        className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {applyingSuggestions ? 'Applying…' : `Apply (${Object.values(acceptedSuggestionIds).filter(Boolean).length})`}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
                    {pendingCategorySuggestions.map(c => (
                      <label key={c.txId} className="flex items-center gap-2.5 text-xs bg-white border border-slate-100 rounded-md px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!acceptedSuggestionIds[c.txId]}
                          onChange={(e) => setAcceptedSuggestionIds(prev => ({ ...prev, [c.txId]: e.target.checked }))}
                          className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="font-semibold text-slate-700 truncate flex-1">{c.description}</span>
                        <span className="text-slate-400 whitespace-nowrap">{c.oldCategory} &rarr; <span className="font-semibold text-blue-700">{c.newCategory}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {categoryData.length > 0 && (
                <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-100">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Category Totals</h4>
                  <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={categoryData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} tickFormatter={(val) => `$${val}`} />
                        <Tooltip 
                          cursor={{ fill: '#f8fafc' }}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                          formatter={(value: number) => [`$${value.toFixed(2)}`, 'Total']}
                        />
                        <Bar dataKey="value" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {filteredTransactions.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  {transactions.length === 0 ? 'No transactions found. Add your first transaction to get started.' : 'No transactions found matching this filter.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-400 tracking-widest">
                      <tr>
                        <th className="px-6 py-3">Date</th>
                        <th className="px-6 py-3">Merchant/Label</th>
                        <th className="px-6 py-3">Category</th>
                        <th className="px-6 py-3 text-right">Amount</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {filteredTransactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((tx) => (
                        <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-3 text-slate-500 whitespace-nowrap">{format(tx.date, 'MMM dd')}</td>
                          <td className="px-6 py-3 font-semibold text-slate-800 whitespace-nowrap">
                            {tx.description || tx.category}
                            {tx.paymentMethod === 'credit' && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200">Credit Card</span>
                            )}
                            {tx.flaggedDuplicateOfId && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 text-amber-600 border border-amber-200">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Possible Duplicate
                                <InfoTooltip iconClassName="text-amber-500">
                                  {tx.flaggedDuplicateReason || 'This looks like it may be counted twice.'}
                                </InfoTooltip>
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                              tx.type === 'income' ? 'bg-emerald-50 text-emerald-600' :
                              tx.type === 'expense' ? 'bg-rose-50 text-rose-600' :
                              tx.type === 'spend' ? 'bg-amber-50 text-amber-600' :
                              tx.type === 'debt_payment' ? 'bg-indigo-50 text-indigo-600' :
                              tx.type === 'transfer' ? 'bg-purple-50 text-purple-600' :
                              'bg-blue-50 text-blue-600'
                            }`}>
                              {tx.type === 'spend' ? 'Spend' : tx.category}
                            </span>
                          </td>
                          <td className={`px-6 py-3 text-right font-bold whitespace-nowrap ${
                            tx.type === 'income' ? 'text-emerald-600' : 
                            tx.type === 'transfer' ? 'text-purple-600' : 
                            'text-slate-700'
                          }`}>
                            {tx.type === 'income' ? '+' : (tx.type === 'transfer' ? '' : '-')}${tx.amount.toFixed(2)}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button 
                              onClick={() => { setEditingTransaction(tx); setIsTxModalOpen(true); }}
                              className="text-slate-300 hover:text-blue-500 transition-colors p-1 mr-1"
                              title="Edit transaction"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteTransaction(tx.id)}
                              className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                              title="Delete transaction"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {Math.ceil(filteredTransactions.length / itemsPerPage) > 1 && (
                    <div className="flex justify-between items-center p-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
                      <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                        Page {currentPage} of {Math.ceil(filteredTransactions.length / itemsPerPage)}
                      </span>
                      <button 
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredTransactions.length / itemsPerPage), p + 1))}
                        disabled={currentPage === Math.ceil(filteredTransactions.length / itemsPerPage)}
                        className="px-3 py-1.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}

        {currentTab === 'debts_budget' && (
          <div className="flex flex-col gap-6">
            {/* Expected Income */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('expected_income')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('expected_income') ? 'rotate-180' : ''}`} />
                  <TrendingUp className="w-4 h-4 text-emerald-500 flex-shrink-0" /> Expected Income
                </h3>
                <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0">
                  Est. Monthly: <span className="text-slate-900">${expectedIncomeMonthlyTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </span>
              </div>
              {expandedFinancialSections.has('expected_income') && (
                <div className="p-4 sm:p-6">
                  <ExpectedIncome
                    incomes={incomes}
                    onEdit={(inc) => { setEditingIncome(inc); setIsBudgetModalOpen(true); }}
                    onDelete={handleDeleteIncome}
                  />
                </div>
              )}
            </section>

            {/* Actual Income (YTD) */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('actual_income')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('actual_income') ? 'rotate-180' : ''}`} />
                  <Banknote className="w-4 h-4 text-emerald-500 flex-shrink-0" /> Actual Income (YTD)
                </h3>
                <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0">
                  Received: <span className="text-slate-900">${ytdIncomeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </span>
              </div>
              {expandedFinancialSections.has('actual_income') && (
                <div className="p-4 sm:p-6">
                  <div className="space-y-3">
                    {ytdIncomeTxs.length === 0 ? (
                      <p className="text-sm text-slate-500">No actual income received yet this year.</p>
                    ) : (
                      ytdIncomeTxs.map(tx => (
                        <div key={tx.id} className="flex justify-between items-center text-sm p-3 bg-slate-50 rounded-lg border border-slate-100 group">
                          <div>
                            <span className="font-semibold text-slate-900 block">{tx.description}</span>
                            <span className="text-xs text-slate-500">{format(tx.date!, 'MMM do, yyyy')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-emerald-600">${tx.amount.toFixed(2)}</span>
                            <button
                              onClick={() => handleDeleteTransaction(tx.id)}
                              className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete income"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Recurring Expenses */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('recurring_expenses')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('recurring_expenses') ? 'rotate-180' : ''}`} />
                  <Calendar className="w-4 h-4 text-rose-500 flex-shrink-0" /> Recurring Expenses
                </h3>
                {recurringExpenseTotals.expected > 0 && (() => {
                  const pct = (recurringExpenseTotals.actual / recurringExpenseTotals.expected) * 100;
                  return (
                    <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0 text-right">
                      ${recurringExpenseTotals.actual.toLocaleString(undefined, { maximumFractionDigits: 0 })} spent of ${recurringExpenseTotals.expected.toLocaleString(undefined, { maximumFractionDigits: 0 })} this month
                      <span className={`ml-1 ${pct >= 100 ? 'text-rose-600' : pct >= 75 ? 'text-amber-600' : 'text-emerald-600'}`}>({pct.toFixed(0)}%)</span>
                    </span>
                  );
                })()}
              </div>
              {expandedFinancialSections.has('recurring_expenses') && (
                <div className="p-4 sm:p-6">
                  {recurringExpenses.length === 0 ? <p className="text-sm text-slate-500">No recurring expenses added yet.</p> : (
                    <div className="space-y-3">
                      {recurringExpenses.map(exp => {
                        const actualSpent = transactions.filter(t =>
                          t.type === 'expense' &&
                          t.date && isSameMonth(t.date, new Date()) &&
                          (t.linkedId === exp.id || t.category.toLowerCase() === exp.category.toLowerCase())
                        ).reduce((sum, t) => sum + t.amount, 0);
                        const progress = Math.min(100, Math.round((actualSpent / exp.amount) * 100));

                        return (
                          <div key={exp.id} className="text-sm p-4 bg-rose-50 rounded-lg border border-rose-100/50 shadow-sm">
                            <div className="flex justify-between items-start mb-2.5">
                              <div>
                                <span className="font-bold text-rose-900 block">{exp.name || exp.category}</span>
                                {exp.name && <span className="text-xs text-rose-600 block">{exp.category}</span>}
                                {exp.notes && <span className="text-[10px] text-rose-500 italic block mt-0.5">{exp.notes}</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider bg-rose-100/50 px-2 py-0.5 rounded">
                                  {formatSchedule(exp.schedule)}
                                </span>
                                <button
                                  onClick={() => { setEditingExpense(exp); setIsBudgetModalOpen(true); }}
                                  className="text-rose-300 hover:text-blue-500 transition-colors p-1"
                                  title="Edit expense"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteExpense(exp.id!)}
                                  className="text-rose-300 hover:text-rose-500 transition-colors p-1"
                                  title="Delete expense"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            <div className="w-full h-1.5 bg-rose-200/50 rounded-full overflow-hidden mb-2">
                              <div
                                className={`h-full ${actualSpent > exp.amount ? 'bg-red-500' : 'bg-rose-500'} transition-all`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="font-bold text-rose-800">Actual: ${actualSpent.toFixed(2)}</span>
                              <span className="text-rose-600 font-medium">Expected: ${exp.amount.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Debts Overview */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('debts_overview')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('debts_overview') ? 'rotate-180' : ''}`} />
                  <Scale className="w-4 h-4 text-slate-500 flex-shrink-0" /> Debts Overview
                </h3>
                {totalDebtAmount > 0 && (() => {
                  const pct = (totalDebtBalance / totalDebtAmount) * 100;
                  return (
                    <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0 text-right">
                      ${totalDebtBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })} owed of ${totalDebtAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      <span className={`ml-1 ${pct >= 80 ? 'text-rose-600' : pct >= 50 ? 'text-amber-600' : 'text-emerald-600'}`}>({pct.toFixed(0)}%)</span>
                    </span>
                  );
                })()}
              </div>
              {expandedFinancialSections.has('debts_overview') && (
                <div className="p-4 sm:p-6">
                  {debts.length === 0 ? <p className="text-sm text-slate-500">No debts added yet. Congratulations!</p> : (
                    <div className="space-y-6">
                      {debtGroups.map(group => {
                        const groupAmount = group.items.reduce((s, d) => s + (d.totalAmount || 0), 0);
                        const groupBalance = group.items.reduce((s, d) => s + (d.currentAmount || 0), 0);
                        const groupPct = groupAmount > 0 ? (groupBalance / groupAmount) * 100 : 0;
                        const GroupIcon = group.icon;
                        return (
                          <div key={group.key}>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
                              <GroupIcon className="w-3.5 h-3.5" /> {group.label}
                              <span className="text-slate-400 normal-case font-semibold">
                                · ${groupBalance.toFixed(2)} {group.revolving ? 'used' : 'owed'} of ${groupAmount.toFixed(2)} {group.revolving ? 'limit' : 'borrowed'} ({groupPct.toFixed(0)}%)
                              </span>
                            </h4>
                            <div className="space-y-4">
                              {group.items.map(debt => {
                                const util = debt.totalAmount > 0 ? (debt.currentAmount / debt.totalAmount) * 100 : 0;
                                return (
                                  <div key={debt.id} className="border border-slate-100 rounded-lg p-4 bg-slate-50">
                                    <div className="flex justify-between mb-2">
                                      <h4 className="font-bold text-slate-900">{debt.name}</h4>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-rose-600 mr-2">${debt.currentAmount.toFixed(2)}</span>
                                        <button
                                          onClick={() => { setEditingDebt(debt); setIsDebtModalOpen(true); }}
                                          className="text-slate-300 hover:text-blue-500 transition-colors p-1"
                                          title="Edit debt"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteDebt(debt.id!)}
                                          className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                                          title="Delete debt"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
                                      <div>
                                        <span className="block text-slate-400">{group.revolving ? 'Credit Limit' : 'Total Borrowed'}</span>
                                        <span className="font-medium">${debt.totalAmount.toFixed(2)}</span>
                                      </div>
                                      <div>
                                        <span className="block text-slate-400">Min Payment</span>
                                        <span className="font-medium">${debt.monthlyMinimum.toFixed(2)}</span>
                                      </div>
                                      <div>
                                        <span className="block text-slate-400">Interest Rate</span>
                                        <span className="font-medium">{debt.interestRate}%</span>
                                      </div>
                                      <div>
                                        <span className="block text-slate-400">Due Date</span>
                                        <span className="font-medium">{formatSchedule(debt.schedule)}</span>
                                      </div>
                                    </div>
                                    {/* Gas tank gauge */}
                                    <div className="w-full h-2 rounded-full overflow-hidden flex relative" title={`${util.toFixed(1)}%`}>
                                      <div className="h-full bg-rose-500 transition-all" style={{ width: `${util}%` }}></div>
                                      <div className="h-full bg-emerald-400 transition-all" style={{ width: `${100 - util}%` }}></div>
                                    </div>
                                    <div className="flex justify-between mt-1 text-[9px] font-bold uppercase tracking-wider">
                                      <span className="text-rose-600">{group.revolving ? `Consumed: ${util.toFixed(0)}%` : `Owed: ${util.toFixed(0)}%`}</span>
                                      <span className="text-emerald-600">{group.revolving ? 'Available' : 'Paid Off'}: ${(debt.totalAmount - debt.currentAmount).toFixed(2)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}

        {currentTab === 'bank_financials' && (
          <div className="flex flex-col gap-6">
            {plaidItems.some(i => i.needsReconnect) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col gap-3">
                <p className="text-sm text-amber-800 font-medium">
                  A bank connection needs reconnecting -- Plaid's login for it has expired, so its transactions and balance can't be synced until you reconnect it. Your local accounts stay linked to the same connection once you do, so nothing needs to be re-matched.
                </p>
                <div className="flex flex-wrap gap-2">
                  {plaidItems.filter((i: PlaidItemDoc) => i.needsReconnect).map((item: PlaidItemDoc) => (
                    <React.Fragment key={item.id}>
                      <PlaidConnectButton
                        householdId={householdId}
                        onSuccess={() => {}}
                        reconnectItemId={item.id}
                        reconnectInstitutionName={item.institutionName}
                        onReconnectSuccess={(institutionName) => alert(`${institutionName} reconnected. Click Sync Real Balances to pull its current balance.`)}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {/* Per-institution connection status -- persisted server-side on
                every sync attempt (success or failure), not just a one-time
                alert() that's gone the moment it's dismissed. Institutions
                needing reconnect already have their own banner above. */}
            {plaidItems.filter((i: PlaidItemDoc) => !i.needsReconnect).length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col gap-2">
                <h3 className="font-bold text-slate-800 text-sm mb-1">Connection Status</h3>
                {plaidItems.filter((i: PlaidItemDoc) => !i.needsReconnect).map((item: PlaidItemDoc) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 text-xs sm:text-sm">
                    <span className="font-medium text-slate-700">{item.institutionName}</span>
                    <div className="flex items-center gap-3">
                      {item.lastSyncAt ? (
                        item.lastSyncOk ? (
                          <span className="text-emerald-700">
                            Synced {formatDistanceToNow(item.lastSyncAt, { addSuffix: true })}
                          </span>
                        ) : (
                          <span className="text-amber-700 text-right" title={item.lastSyncMessage || undefined}>
                            Last attempt {formatDistanceToNow(item.lastSyncAt, { addSuffix: true })} failed
                            {item.lastSyncMessage ? `: ${item.lastSyncMessage}` : ''}
                          </span>
                        )
                      ) : (
                        <span className="text-slate-400">Not synced yet</span>
                      )}
                      <button
                        onClick={() => handleManageAccounts(item.id, item.institutionName)}
                        disabled={managingAccountsFor === item.id}
                        title="Check for accounts at this bank that were skipped when you first connected, or opened since -- no Plaid Link session needed, just a quick lookup"
                        className="text-blue-600 hover:text-blue-800 font-semibold underline decoration-dotted underline-offset-2 disabled:opacity-50 whitespace-nowrap"
                      >
                        {managingAccountsFor === item.id ? 'Checking…' : 'Manage Accounts'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Bank Accounts */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('bank_accounts')}
                className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('bank_accounts') ? 'rotate-180' : ''}`} />
                  <Banknote className="w-4 h-4 text-blue-500 flex-shrink-0" /> Bank Accounts
                </h3>
                <div className="flex items-center gap-4 text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0">
                  <span className="text-right">
                    Liquid: <span className="text-slate-900">${totalBankBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {totalInvestmentBalance > 0 && (
                      <span className="text-violet-600 ml-2">
                        Invested: ${totalInvestmentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              {expandedFinancialSections.has('bank_accounts') && (
                <div className="p-4">
                  {bankAccounts.length === 0 ? <p className="text-sm text-slate-500">No bank accounts added.</p> : (
                    <div className="space-y-6">
                      {bankAccountGroups.map(group => {
                        const groupTotal = group.accounts.reduce((s, a) => s + (a.currentBalance || 0), 0);
                        const GroupIcon = group.icon;
                        return (
                          <div key={group.key}>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
                              <GroupIcon className="w-3.5 h-3.5" /> {group.label}
                              <span className="text-slate-400 normal-case font-semibold">· ${groupTotal.toFixed(2)}</span>
                            </h4>
                            <div className="space-y-4">
                              {group.accounts.map(account => {
                                const accountTxs = transactions.filter(t => t.bankAccountId === account.id).sort((a, b) => {
                                  const dateA = a.date ? new Date(a.date).getTime() : 0;
                                  const dateB = b.date ? new Date(b.date).getTime() : 0;
                                  return dateB - dateA;
                                });
                                const isExpanded = expandedBankAccounts.has(account.id);
                                return (
                                  <div key={account.id} className="border border-slate-100 rounded-lg overflow-hidden shadow-sm">
                                    <div className="bg-slate-50 p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors group" onClick={() => toggleBankAccount(account.id)}>
                                      <div className="flex justify-between items-center mb-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                          <div className="min-w-0">
                                            <h4 className="font-bold text-slate-800 text-sm truncate">{account.name}</h4>
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                                {account.type.replace('investment_', '').replace('_', ' ')}
                                              </span>
                                              {account.isJoint && (
                                                <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                                  Joint
                                                </span>
                                              )}
                                              {account.expectedAnnualReturn !== undefined && (
                                                <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                                  {account.expectedAnnualReturn}% Return
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={(e) => { e.stopPropagation(); setFeeModalAccount(account); }} className="text-slate-400 hover:text-amber-500 p-1" title="Record a fee (overdraft, NSF, service fee...)"><AlertTriangle className="w-3.5 h-3.5" /></button>
                                            <button onClick={(e) => { e.stopPropagation(); setEditingBankAccount(account); setIsBankAccountModalOpen(true); }} className="text-slate-400 hover:text-blue-500 p-1" title="Edit account"><Edit2 className="w-3.5 h-3.5" /></button>
                                          </div>
                                          <span className="text-sm font-bold text-slate-900">${account.currentBalance.toFixed(2)}</span>
                                        </div>
                                      </div>
                                      <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden flex">
                                        <div className="h-full bg-blue-500 w-full"></div>
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      accountTxs.length === 0 ? (
                                        <div className="p-3 text-xs text-slate-500 italic">No transactions.</div>
                                      ) : (
                                        <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                                          {accountTxs.map(tx => {
                                            const isDebit = tx.type !== 'income';
                                            return (
                                              <div key={tx.id} className="p-2.5 text-xs flex justify-between items-center bg-white">
                                                <div className="truncate mr-2">
                                                  <span className="font-medium text-slate-900 block truncate">{tx.description || tx.category}</span>
                                                  <span className="text-[9px] text-slate-500">{tx.date ? format(new Date(tx.date), 'MMM do') : ''}</span>
                                                </div>
                                                <div className="text-right shrink-0">
                                                  <span className={`font-bold ${isDebit ? 'text-slate-700' : 'text-emerald-600'}`}>
                                                    {isDebit ? '-' : '+'}${tx.amount.toFixed(2)}
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Lines of Credit */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('lines_of_credit')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('lines_of_credit') ? 'rotate-180' : ''}`} />
                  <CreditCard className="w-4 h-4 text-emerald-500 flex-shrink-0" /> Lines of Credit
                </h3>
                {totalLocLimit > 0 && (() => {
                  const locUtil = (totalLocBalance / totalLocLimit) * 100;
                  return (
                    <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0 text-right">
                      ${totalLocBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })} used of ${totalLocLimit.toLocaleString(undefined, { maximumFractionDigits: 0 })} limit
                      <span className={`ml-1 ${locUtil >= 80 ? 'text-rose-600' : locUtil >= 50 ? 'text-amber-600' : 'text-emerald-600'}`}>({locUtil.toFixed(0)}%)</span>
                    </span>
                  );
                })()}
              </div>
              {expandedFinancialSections.has('lines_of_credit') && (
                <div className="p-4">
                  {(() => {
                    const locs = debts.filter(d => d.type === 'line_of_credit');
                    if (locs.length === 0) return <p className="text-sm text-slate-500">No lines of credit.</p>;
                    return (
                      <div className="space-y-4">
                        {locs.map(loc => {
                          const util = loc.totalAmount > 0 ? (loc.currentAmount / loc.totalAmount) * 100 : 0;
                          return (
                            <div key={loc.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50 shadow-sm relative group">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="font-bold text-slate-900 text-sm">{loc.name}</h4>
                                  <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                                    Limit: ${loc.totalAmount.toFixed(0)}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-bold text-rose-600 block">Owe: ${loc.currentAmount.toFixed(2)}</span>
                                  <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                                    <button onClick={(e) => { e.stopPropagation(); setEditingDebt(loc); setDebtModalInitialType('line_of_credit'); setIsDebtModalOpen(true); }} className="text-slate-400 hover:text-blue-500 p-1"><Edit2 className="w-3 h-3"/></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteDebt(loc.id!); }} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-3 h-3"/></button>
                                  </div>
                                </div>
                              </div>
                              {/* Gas tank gauge */}
                              <div className="w-full h-2 rounded-full overflow-hidden flex relative" title={`Utilized: ${util.toFixed(1)}%`}>
                                <div className="h-full bg-rose-500 transition-all" style={{ width: `${util}%` }}></div>
                                <div className="h-full bg-emerald-400 transition-all" style={{ width: `${100 - util}%` }}></div>
                              </div>
                              <div className="flex justify-between mt-1 text-[9px] font-bold uppercase tracking-wider">
                                <span className="text-rose-600">Consumed</span>
                                <span className="text-emerald-600">Available: ${(loc.totalAmount - loc.currentAmount).toFixed(2)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </section>

            {/* Credit Cards */}
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => toggleFinancialSection('credit_cards')}
                className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
              >
                <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expandedFinancialSections.has('credit_cards') ? 'rotate-180' : ''}`} />
                  <CreditCard className="w-4 h-4 text-rose-500 flex-shrink-0" /> Credit Cards
                </h3>
                {totalCreditLimit > 0 && (() => {
                  const cardUtil = (totalCreditBalance / totalCreditLimit) * 100;
                  return (
                    <span className="text-xs sm:text-sm font-bold text-slate-600 flex-shrink-0 text-right">
                      ${totalCreditBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })} used of ${totalCreditLimit.toLocaleString(undefined, { maximumFractionDigits: 0 })} limit
                      <span className={`ml-1 ${cardUtil >= 80 ? 'text-rose-600' : cardUtil >= 50 ? 'text-amber-600' : 'text-emerald-600'}`}>({cardUtil.toFixed(0)}%)</span>
                    </span>
                  );
                })()}
              </div>
              {expandedFinancialSections.has('credit_cards') && (
                <div className="p-4">
                  {(() => {
                    const creditCards = debts.filter(d => d.type === 'credit_card');
                    if (creditCards.length === 0) return <p className="text-sm text-slate-500">No credit cards.</p>;
                    return (
                      <div className="space-y-4">
                        {creditCards.map(card => {
                          const cardTxs = transactions.filter(t => t.creditCardId === card.id || (t.linkedId === card.id && t.type === 'debt_payment')).sort((a, b) => {
                            const dateA = a.date ? new Date(a.date).getTime() : 0;
                            const dateB = b.date ? new Date(b.date).getTime() : 0;
                            return dateB - dateA;
                          });
                          const isExpanded = expandedCreditCards.has(card.id);
                          const util = card.totalAmount > 0 ? (card.currentAmount / card.totalAmount) * 100 : 0;
                          return (
                            <div key={card.id} className="border border-slate-100 rounded-lg overflow-hidden shadow-sm">
                              <div className="bg-slate-50 p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors group" onClick={() => toggleCreditCard(card.id)}>
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex items-center gap-2">
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    <div>
                                      <h4 className="font-bold text-slate-800 text-sm">{card.name}</h4>
                                      <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                                        Limit: ${card.totalAmount.toFixed(0)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-sm font-bold text-rose-600 block">Owe: ${card.currentAmount.toFixed(2)}</span>
                                    <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                                      <button onClick={(e) => { e.stopPropagation(); setEditingDebt(card); setIsCreditCardModalOpen(true); }} className="text-slate-400 hover:text-blue-500 p-1"><Edit2 className="w-3 h-3"/></button>
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteDebt(card.id!); }} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-3 h-3"/></button>
                                    </div>
                                  </div>
                                </div>
                                {/* Gas tank gauge */}
                                <div className="w-full h-2 rounded-full overflow-hidden flex relative" title={`Utilized: ${util.toFixed(1)}%`}>
                                  <div className="h-full bg-rose-500 transition-all" style={{ width: `${util}%` }}></div>
                                  <div className="h-full bg-emerald-400 transition-all" style={{ width: `${100 - util}%` }}></div>
                                </div>
                                <div className="flex justify-between mt-1 text-[9px] font-bold uppercase tracking-wider">
                                  <span className="text-rose-600">Consumed</span>
                                  <span className="text-emerald-600">Available: ${(card.totalAmount - card.currentAmount).toFixed(2)}</span>
                                </div>
                              </div>
                              {isExpanded && (
                                cardTxs.length === 0 ? (
                                  <div className="p-3 text-xs text-slate-500 italic">No transactions.</div>
                                ) : (
                                  <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                                    {cardTxs.map(tx => {
                                      const isDebit = tx.creditCardId === card.id;
                                      return (
                                        <div key={tx.id} className="p-2.5 text-xs flex justify-between items-center bg-white">
                                          <div className="truncate mr-2">
                                            <span className="font-medium text-slate-900 block truncate">{tx.description || tx.category}</span>
                                            <span className="text-[9px] text-slate-500">{tx.date ? format(new Date(tx.date), 'MMM do') : ''}</span>
                                          </div>
                                          <div className="text-right shrink-0">
                                            <span className={`font-bold ${isDebit ? 'text-rose-600' : 'text-emerald-600'}`}>
                                              {isDebit ? '+' : '-'}${tx.amount.toFixed(2)}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </section>
          </div>
        )}

        {currentTab === 'ai_strategy' && (
          <div className="flex-1 min-h-0">
            <AIDashboard reports={reports} userId={user.uid} householdId={householdId} />
          </div>
        )}

        {currentTab === 'settings' && householdId && (
          <div className="flex-1 min-h-0">
            <Settings householdId={householdId} userId={user.uid} user={user} />
          </div>
        )}

        {currentTab === 'net_worth' && (
          <NetWorth bankAccounts={bankAccounts} debts={debts} />
        )}

        {currentTab === 'vault' && (
          <div className="flex-1 min-h-0">
            <Vault 
              userId={user.uid} 
              householdId={householdId || ''}
              onOpenChat={(id) => { 
                setActiveConversationId(id); 
                setIsChatModalOpen(true); 
              }} 
            />
          </div>
        )}

        {currentTab === 'goals_planner' && (
          <GoalsPlanner 
            goals={goals}
            wishlist={wishlist}
            householdId={householdId}
            userId={user.uid}
            onEditGoal={(g) => { setEditingGoal(g); setIsGoalModalOpen(true); }}
            onEditWishlist={(w) => { setEditingWishlistItem(w); setIsWishlistModalOpen(true); }}
          />
        )}

        {currentTab === 'planning_schedule' && (
          <PlanningScheduleDashboard
            plannedPayments={plannedPayments}
            recurringExpenses={recurringExpenses}
            debts={debts}
            goals={goals}
            incomes={incomes}
            transactions={transactions}
            paymentFiles={paymentFiles}
            dismissedOccurrences={dismissedOccurrences}
            householdId={householdId}
            onAddSchedule={() => { setEditingPlannedPayment(null); setIsPlannedPaymentModalOpen(true); }}
            onEditPayment={(p) => { setEditingPlannedPayment(p); setIsPlannedPaymentModalOpen(true); }}
            onMarkAsPaid={() => { /* no-op — manual complete is handled inside PlanningScheduleDashboard */ }}
            commitmentPaidMap={commitmentPaidMap}
          />
        )}

        {currentTab === 'debt_crush' && (
          <DebtCrush userId={user.uid} householdId={householdId} />
        )}

        {currentTab === 'reconcile' && (
          <ReconcileDashboard
            householdId={householdId}
            userId={user.uid}
            bankAccounts={bankAccounts}
            debts={debts}
            transactions={transactions}
            bankStatements={bankStatements}
          />
        )}

      </main>

      {/* Floating Action Button for AI Chat */}
      <button
        onClick={() => {
          setActiveConversationId(null);
          setIsChatModalOpen(true);
        }}
        className="fixed bottom-24 md:bottom-8 right-4 md:right-8 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 hover:scale-105 transition-all z-50 group"
        title="Chat with AI Strategist"
      >
        <Bot className="w-6 h-6" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Ask Strategist
        </span>
      </button>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around items-center h-16 px-2 z-40 pb-safe">
        <button 
          onClick={() => setCurrentTab('overview')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'overview' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Wallet className="w-5 h-5" />
          <span className="text-[10px] font-medium">Overview</span>
        </button>
        <button 
          onClick={() => setCurrentTab('bank_financials')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'bank_financials' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Banknote className="w-5 h-5" />
          <span className="text-[10px] font-medium">Financials</span>
        </button>
        <button
          onClick={() => setCurrentTab('reconcile')}
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'reconcile' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <UploadCloud className="w-5 h-5" />
          <span className="text-[10px] font-medium">Reconcile</span>
        </button>
        <button
          onClick={() => setCurrentTab('debts_budget')}
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'debts_budget' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <CreditCard className="w-5 h-5" />
          <span className="text-[10px] font-medium">Budget</span>
        </button>
        <button 
          onClick={() => setCurrentTab('net_worth')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'net_worth' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Scale className="w-5 h-5" />
          <span className="text-[10px] font-medium">Worth</span>
        </button>
        <button 
          onClick={() => setCurrentTab('goals_planner')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'goals_planner' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Target className="w-5 h-5" />
          <span className="text-[10px] font-medium">Planner</span>
        </button>
        <button 
          onClick={() => setCurrentTab('planning_schedule')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'planning_schedule' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Calendar className="w-5 h-5" />
          <span className="text-[10px] font-medium">Schedule</span>
        </button>
        <button 
          onClick={() => setCurrentTab('ai_strategy')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'ai_strategy' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <Bot className="w-5 h-5" />
          <span className="text-[10px] font-medium">AI Hub</span>
        </button>
        <button 
          onClick={() => setCurrentTab('debt_crush')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'debt_crush' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <TrendingDown className="w-5 h-5" />
          <span className="text-[10px] font-medium">Debt Crush</span>
        </button>
        <button 
          onClick={() => setCurrentTab('vault')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'vault' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <MessageSquare className="w-5 h-5" />
          <span className="text-[10px] font-medium">Vault</span>
        </button>
        <button 
          onClick={() => setCurrentTab('settings')} 
          className={`flex flex-col items-center justify-center w-12 gap-1 ${currentTab === 'settings' ? 'text-blue-600' : 'text-slate-500'}`}
        >
          <SettingsIcon className="w-5 h-5" />
          <span className="text-[10px] font-medium">Settings</span>
        </button>
      </nav>

      <TransactionModal 
        userId={user.uid} 
        userEmail={user.email || 'Unknown'}
        householdId={householdId}
        isOpen={isTxModalOpen} 
        onClose={() => { setIsTxModalOpen(false); setEditingTransaction(null); }} 
        goals={goals}
        debts={debts}
        bankAccounts={bankAccounts}
        recurringExpenses={recurringExpenses}
        plannedPayments={plannedPayments}
        commitmentPaidMap={commitmentPaidMap}
        onOpenGoalModal={() => { setIsTxModalOpen(false); setEditingGoal(null); setIsGoalModalOpen(true); }}
        onOpenDebtModal={() => { setIsTxModalOpen(false); setDebtModalInitialType('loan'); setIsDebtModalOpen(true); }}
        onOpenBudgetModal={() => { setIsTxModalOpen(false); setIsBudgetModalOpen(true); }}
        editingTransaction={editingTransaction}
      />
      <GoalModal userId={user.uid} householdId={householdId} isOpen={isGoalModalOpen} onClose={() => { setIsGoalModalOpen(false); setEditingGoal(null); }} editingGoal={editingGoal} />
      <DebtModal userId={user.uid} householdId={householdId} isOpen={isDebtModalOpen} onClose={() => { setIsDebtModalOpen(false); setEditingDebt(null); }} editingDebt={editingDebt} initialDebtType={debtModalInitialType} />
      <BankAccountModal userId={user.uid} householdId={householdId} isOpen={isBankAccountModalOpen} onClose={() => { setIsBankAccountModalOpen(false); setEditingBankAccount(null); }} editingAccount={editingBankAccount} />
      <AccountFeeModal userId={user.uid} userEmail={user.email || 'Unknown'} householdId={householdId} isOpen={!!feeModalAccount} onClose={() => setFeeModalAccount(null)} account={feeModalAccount} />
      <CreditCardModal userId={user.uid} householdId={householdId} isOpen={isCreditCardModalOpen} onClose={() => { setIsCreditCardModalOpen(false); setEditingDebt(null); }} editingCard={editingDebt} />
      <BudgetModal userId={user.uid} householdId={householdId} isOpen={isBudgetModalOpen} onClose={() => { setIsBudgetModalOpen(false); setEditingIncome(null); setEditingExpense(null); }} editingIncome={editingIncome} editingExpense={editingExpense} bankAccounts={bankAccounts} />
      <WishlistModal userId={user.uid} householdId={householdId} isOpen={isWishlistModalOpen} onClose={() => { setIsWishlistModalOpen(false); setEditingWishlistItem(null); }} editingItem={editingWishlistItem} />
      <PlannedPaymentModal userId={user.uid} householdId={householdId} isOpen={isPlannedPaymentModalOpen} onClose={() => { setIsPlannedPaymentModalOpen(false); setEditingPlannedPayment(null); }} editingPayment={editingPlannedPayment} />
      
      {plaidMappingData && (
        <PlaidAccountMapper
          plaidAccounts={plaidMappingData.accounts}
          existingAccounts={bankAccounts}
          existingDebts={debts}
          institutionName={plaidMappingData.institutionName}
          onSave={handlePlaidSaveMappings}
          onCancel={() => setPlaidMappingData(null)}
        />
      )}

      {manageAccountsData && (
        <PlaidAccountMapper
          plaidAccounts={manageAccountsData.accounts}
          existingAccounts={bankAccounts}
          existingDebts={debts}
          institutionName={manageAccountsData.institutionName}
          onSave={handleSaveNewMappings}
          onCancel={() => setManageAccountsData(null)}
        />
      )}
      
      <AIChatModal 
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        userId={user.uid}
        householdId={householdId}
        conversationId={activeConversationId}
      />
    </>
  );
}
